/**
 * Resource Regeneration Engine - Handles HP/SP/MP recovery rates,
 * CON stat bonuses, Bard Songs (Focus/Regen), Paladin Vigor & Healing Chant,
 * and DoT (Meteor burn) ticks.
 */

import { EntityState } from '../../core/Types';
import { EventBus } from '../../core/EventBus';

export class ResourceRegenEngine {
  public update(entity: EntityState, dt: number, hasBardVigor: boolean): void {
    if (entity.isDead) return;

    const res = entity.resources;
    const con = entity.effectiveStats?.con || 60;
    const conHpBonus = Math.max(0, (con - 50) * 0.05);
    const conSpBonus = Math.max(0, (con - 50) * 0.10);

    // Sum active song/chant regen bonuses on entity
    let buffHpRegen = 0;
    let buffSpRegen = hasBardVigor ? 4.0 : 0.0;
    let buffMpRegen = 0;

    for (const b of entity.buffs) {
      if (b.hpRegenBonus) buffHpRegen += b.hpRegenBonus;
      if (b.spRegenBonus) buffSpRegen += b.spRegenBonus;
      if (b.mpRegenBonus) buffMpRegen += b.mpRegenBonus;

      // Tick timed buffs (e.g. SOS 10s, Mana Shield 60s)
      if (b.duration > 0) {
        b.elapsed += dt;
      }
    }

    // Remove expired timed buffs (non-toggle)
    entity.buffs = entity.buffs.filter((b) => b.duration < 0 || b.elapsed < b.duration);

    if (entity.inCombat) {
      const hpRate = 0.5 + conHpBonus + buffHpRegen;
      const spRate = 1.5 + conSpBonus + buffSpRegen;
      const mpRate = 0.8 + buffMpRegen;

      res.hp = Math.min(res.maxHp, res.hp + hpRate * dt);
      res.sp = Math.min(res.maxSp, res.sp + spRate * dt);
      res.mp = Math.min(res.maxMp, res.mp + mpRate * dt);
    } else {
      const hpRate = 5.0 + conHpBonus + buffHpRegen;
      const spRate = 12.0 + conSpBonus + buffSpRegen;
      const mpRate = 8.0 + buffMpRegen;

      res.hp = Math.min(res.maxHp, res.hp + hpRate * dt);
      res.sp = Math.min(res.maxSp, res.sp + spRate * dt);
      res.mp = Math.min(res.maxMp, res.mp + mpRate * dt);
    }

    // Tick active DoT effect (e.g. Wizard Meteor burn)
    if (entity.dotEffect) {
      entity.dotEffect.remaining -= dt;
      entity.dotEffect.tickAccum += dt;
      if (entity.dotEffect.tickAccum >= 1.0) {
        entity.dotEffect.tickAccum -= 1.0;
        const dmg = Math.round(entity.dotEffect.damagePerSec);
        res.hp = Math.max(0, res.hp - dmg);
        EventBus.emit('COMBAT_HIT', {
          attackerId: entity.dotEffect.sourceId,
          attackerName: entity.dotEffect.sourceName,
          targetId: entity.id,
          targetName: entity.name,
          damage: dmg,
          damageType: 'holy',
          isCrit: false,
          isFriendlyFire: false,
        });
      }
      if (entity.dotEffect.remaining <= 0) {
        entity.dotEffect = null;
      }
    }
  }
}

export const ResourceRegen = new ResourceRegenEngine();
